"use client"

import { useState, type FormEvent } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { captureChatError } from "@/lib/sentry-chat"
import { validatePhone } from "@/lib/phone"

interface OutOfHoursFormProps {
  conversationId: number | null
  /**
   * The per-contact widget JWT (`session.source_id`). Sent as `X-Auth-Token` so
   * glue can confirm this session owns `conversationId` before escalating it.
   */
  authToken: string | null
  /** Current page country — used to validate phone numbers with the correct country default. */
  country?: string
  /** Fires after a successful submission so the parent can swap in a thank-you turn. */
  onSubmitted: (values: {
    email: string
    phone: string
    message: string
  }) => void
}

/**
 * Shown inline inside the chat panel when the user asks for (or the bot
 * triggers) a human handoff outside business hours. Collects email,
 * optional phone, and an optional free-text note, and POSTs them to
 * /api/chat/handoff with `out_of_hours: true` so the team sees a
 * prioritized follow-up task in Chatwoot next business day.
 */
export function OutOfHoursForm({
  conversationId,
  authToken,
  country,
  onSubmitted,
}: OutOfHoursFormProps) {
  const t = useTranslations("chat.ui.outOfHours")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [message, setMessage] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!emailValid) {
      setError(t("errorEmail"))
      return
    }
    // Validate phone only when the user has typed something
    const trimmedPhone = phone.trim()
    if (trimmedPhone && !validatePhone(trimmedPhone, country)) {
      setError(t("errorPhone"))
      return
    }
    setError(null)
    setSubmitting(true)
    const trimmed = {
      email: email.trim(),
      phone: phone.trim(),
      message: message.trim(),
    }
    try {
      const res = await fetch("/api/chat/handoff", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { "X-Auth-Token": authToken } : {}),
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          out_of_hours: true,
          ...trimmed,
        }),
      })
      if (!res.ok) throw new Error(`handoff failed (${res.status})`)
      onSubmitted(trimmed)
    } catch (err) {
      // We just lost a lead — capture so on-call can retry / contact
      // the user directly via the email they did manage to type.
      captureChatError(err, {
        conversation_id: conversationId,
        handed_off: true,
        extra: {
          form: "out-of-hours",
          email_present: Boolean(trimmed.email),
          phone_present: Boolean(trimmed.phone),
          message_length: trimmed.message.length,
        },
      })
      setError(t("errorGeneric"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border-deep-blue-100 bg-deep-blue-50/60 flex flex-col gap-2 rounded-2xl border p-3"
    >
      <p className="text-deep-blue-950 text-sm font-semibold">{t("title")}</p>
      <p className="text-deep-blue-900 type-body-sm">{t("subtitle")}</p>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("emailPlaceholder")}
        className={cn(
          "text-foreground rounded-[12px] border border-neutral-200 bg-white px-3 py-2 text-base transition-colors outline-none placeholder:text-neutral-400 sm:text-sm",
          "focus:border-deep-blue-800 focus:ring-deep-blue-50 focus:ring-2"
        )}
      />
      <input
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder={t("phonePlaceholder")}
        className={cn(
          "text-foreground rounded-[12px] border border-neutral-200 bg-white px-3 py-2 text-base transition-colors outline-none placeholder:text-neutral-400 sm:text-sm",
          "focus:border-deep-blue-800 focus:ring-deep-blue-50 focus:ring-2"
        )}
      />
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t("messagePlaceholder")}
        rows={2}
        className={cn(
          "text-foreground resize-none rounded-[12px] border border-neutral-200 bg-white px-3 py-2 text-base transition-colors outline-none placeholder:text-neutral-400 sm:text-sm",
          "focus:border-deep-blue-800 focus:ring-deep-blue-50 focus:ring-2"
        )}
      />
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting || !emailValid}
        className={cn(
          "bg-deep-blue-950 hover:bg-deep-blue-900 rounded-full px-3 py-2 text-sm font-medium text-white transition-colors",
          "disabled:opacity-50"
        )}
      >
        {submitting ? t("submitting") : t("submit")}
      </button>
    </form>
  )
}
